import React, { useState, useRef, useEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Button } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ConfirmationDialog from "../../../../../components/DialogPopup/ConfirmationModal";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

// PDF.js worker configuration removed - let react-pdf handle it automatically

// Fixed PDF dimensions for coordinate consistency
const PDF_WIDTH = 800;
const PDF_HEIGHT = 600;

const PdfEditorAdmin = ({
  pdfFile, // Can be File object or URL string
  pdfName,
  onSave,
  onClose,
  isReadOnly = false, // New prop for read-only mode
  showPreview = true, // New prop to control preview visibility
  initialFields = undefined, // New prop for pre-existing fields (undefined means not provided)
  onFieldsChange = null, // New prop to sync fields with parent
}) => {
  // Admin mode: full control over fields (subject to per-field lock state)
  const allowFieldEditing = true;
  const allowFieldManipulation = !isReadOnly;

  // Debug logging for props
  console.log('PdfEditorAdmin - Props received:', {
    pdfFile,
    pdfName,
    isReadOnly,
    showPreview,
    initialFieldsCount: initialFields?.length,
    initialFields: initialFields?.map(f => ({ id: f.id, type: f.type, label: f.label, x: f.x, y: f.y }))
  });
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [fields, setFields] = useState(Array.isArray(initialFields) ? initialFields : []);
  const [lockWarningOpen, setLockWarningOpen] = useState(false);
  
  // Debug initial fields
  useEffect(() => {
    console.log('PdfEditorAdmin - Initial fields received:', initialFields);
    console.log('PdfEditorAdmin - Initial fields count:', initialFields?.length);
    if (initialFields && initialFields.length > 0) {
      initialFields.forEach((field, index) => {
        console.log(`Initial Field ${index + 1}:`, {
          type: field.type,
          label: field.label,
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          id: field.id,
          isExtracted: field.isExtracted
        });
      });
    }
  }, [initialFields]);

  // Keep fields state in sync when initialFields change (important for clinician reloads)
  useEffect(() => {
    if (Array.isArray(initialFields)) {
      setFields(initialFields);
    }
  }, [initialFields?.length, JSON.stringify(initialFields)]);
  
  // Debug logging for field changes
  useEffect(() => {
    console.log('PdfEditorAdmin - Fields updated:', {
      fieldsCount: fields.length,
      fields: fields.map(f => ({ id: f.id, type: f.type, label: f.label, x: f.x, y: f.y })),
      isReadOnly,
      allowFieldEditing,
      allowFieldManipulation,
      renderedDimensions: {
        width: getRenderedPageWidth(),
        height: getRenderedPageHeight()
      }
    });
  }, [fields, isReadOnly, allowFieldEditing, allowFieldManipulation]);
  const [selectedField, setSelectedField] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [modifiedPdfUrl, setModifiedPdfUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [pdfFileObject, setPdfFileObject] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(null);
  const [pdfPageSize, setPdfPageSize] = useState({ width: 612, height: 792 }); // default Letter size in points
  const [isPdfSaved, setIsPdfSaved] = useState(false); // Track if PDF has been saved to hide overlays
  const [requiredPromptFieldId, setRequiredPromptFieldId] = useState(null);

  const pdfContainerRef = useRef(null);
  // Use viewer width to render pages responsively (prevents left/right crop)
  const getRenderedPageWidth = () => {
    const padding = 5; // matches pdf-viewer padding
    const containerW = pdfContainerRef.current?.clientWidth || PDF_WIDTH;
    // ensure we don't exceed designed PDF width but also fit into container
    return Math.min(PDF_WIDTH, Math.max(0, containerW - padding));
  };
  const getRenderedPageHeight = () => PDF_HEIGHT;
    // Convert URL to File object if needed
  useEffect(() => {
    const loadPdfFile = async () => {
      if (!pdfFile) return;

      // If pdfFile is already a File object, use it directly
      if (pdfFile instanceof File) {
        console.log('PDF File object received:', pdfFile.name, pdfFile.size, pdfFile.type);

        // Validate file type
        if (pdfFile.type !== 'application/pdf') {
          console.error('Invalid file type:', pdfFile.type);
          setPdfLoadError(new Error('Invalid file type. Please upload a PDF file.'));
          setIsLoadingPdf(false);
          return;
        }

        // Validate file size (max 10MB)
        if (pdfFile.size > 10 * 1024 * 1024) {
          console.error('File too large:', pdfFile.size);
          setPdfLoadError(new Error('File too large. Please upload a PDF smaller than 10MB.'));
          setIsLoadingPdf(false);
          return;
        }

        // Create object URL for better compatibility
        const objectUrl = URL.createObjectURL(pdfFile);
        console.log('Created object URL:', objectUrl);

        setPdfFileObject(pdfFile);
        setIsLoadingPdf(false);
        return;
      }

      // If pdfFile is a URL string, fetch and convert to File
      if (typeof pdfFile === 'string') {
        setIsLoadingPdf(true);
        try {
          const response = await fetch(pdfFile);
          if (!response?.ok) {
            throw new Error(`Failed to fetch PDF: ${response?.statusText}`);
          }
          const blob = await response?.blob();
          const fileName = pdfName || pdfFile.split('/').pop() || 'document.pdf';
          const file = new File([blob], fileName, { type: 'application/pdf' });
          setPdfFileObject(file);
        } catch (error) {
          console.error('Error loading PDF from URL:', error);
          alert('Failed to load PDF. Please check the URL and try again.');
        } finally {
          setIsLoadingPdf(false);
        }
      }
    };

    loadPdfFile();
  }, [pdfFile, pdfName]);

  const renderLockWarningDialog = () => (
    <ConfirmationDialog
      open={lockWarningOpen}
      handleClose={() => setLockWarningOpen(false)}
      title={"Action not allowed"}
      message={"This field has already been filled so cannot be modified."}
      showConfirmButton={false}
      showCancelButton={true}
      cancelText="OK"
    />
  );

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log('PDF loaded successfully with', numPages, 'pages');
    setNumPages(numPages);

    // Read-only mode: use only initialFields
    if (isReadOnly) {
      console.log('Read-only mode: Using only admin-configured fields (no extraction)');
      const makeKey = (f) => f?.id || `${f?.page}-${f?.x}-${f?.y}-${f?.type}-${f?.width}-${f?.height}`;
      const unique = Array.isArray(initialFields)
        ? Array.from(new Map(initialFields.map(f => [makeKey(f), f])).values())
        : [];
      setFields(unique);
      return;
    }

    // Admin edit mode:
    // If initialFields is provided (even empty array), use it as the source of truth and skip extraction
    if (Array.isArray(initialFields)) {
      const makeKey = (f) => f?.id || `${f?.page}-${f?.x}-${f?.y}-${f?.type}-${f?.width}-${f?.height}`;
      const unique = Array.from(new Map(initialFields.map(f => [makeKey(f), f])).values());
      setFields(unique);
      return;
    }

    // Otherwise, extract existing fields from the PDF (no admin-configured template yet)
    console.log('Admin edit mode: Extracting form fields from PDF');
    extractFormFields();
  };

  // Extract existing form fields from uploaded PDF
  const extractFormFields = async () => {
    if (!pdfFileObject) return;

    setIsLoadingFields(true);
    try {
      const arrayBuffer = await pdfFileObject.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const form = pdfDoc.getForm();
      const formFields = form.getFields();

      const firstPage = pdfDoc.getPage(0);
      const { width: pageWidth, height: pageHeight } = firstPage.getSize();
      setPdfPageSize({ width: pageWidth, height: pageHeight });

      if (formFields?.length === 0 && initialFields?.length === 0) {
        setIsLoadingFields(false);
        return;
      }

      const extractedFields = [];

      // Use fixed PDF dimensions for consistent scaling
      const scaleX = PDF_WIDTH / pageWidth;
      const scaleY = PDF_HEIGHT / pageHeight;

      formFields?.forEach((field, index) => {
        try {
          const fieldName = field?.getName();
          const widgets = field?.acroField.getWidgets();

          if (widgets.length > 0) {
            const widget = widgets[0];
            const rect = widget.getRectangle();

            // Convert PDF coordinates to HTML coordinates (pixels) using fixed dimensions
            const x = rect.x * scaleX;
            const y = PDF_HEIGHT - (rect.y + rect.height) * scaleY;
            const width = rect.width * scaleX;
            const height = rect.height * scaleY;

            // Determine field type and value
            let fieldTypeStr = "text";
            let fieldValue = "";

            if (field?.constructor.name === "PDFTextField") {
              fieldTypeStr = "text";
              fieldValue = field?.getText() || "";
            } else if (fieldName.toLowerCase().includes("signature")) {
              fieldTypeStr = "signature";
            }

            // Find which page this field is on
            const pages = pdfDoc.getPages();
            let fieldPage = 1;
            for (let i = 0; i < pages?.length; i++) {
              const page = pages[i];
              const annotations = page.node.Annots();
              if (
                annotations &&
                annotations.asArray().some((annot) => annot === widget.dict)
              ) {
                fieldPage = i + 1;
                break;
              }
            }

            extractedFields.push({
              id: `extracted_${index}_${Date.now()}`,
              type: fieldTypeStr,
              x: Math.max(0, x),
              y: Math.max(0, y),
              width: Math.max(40, width),
              height: Math.max(20, height),
              label: fieldName,
              value: fieldValue,
              page: fieldPage,
              originalFieldName: fieldName,
              isExtracted: true,
            });
          }
        } catch (error) {
          console.warn("Error processing field:", error);
        }
      });

      // For read-only mode, only use initial fields
      // For edit mode (admins), use admin-configured fields if provided; otherwise use extracted
      if (isReadOnly) {
        setFields(initialFields);
        console.log('Read-only mode: Using only admin-configured fields:', initialFields);
      } else {
        if (Array.isArray(initialFields)) {
          setFields(initialFields);
          console.log('Edit mode: Using admin-configured fields (skip extracted)');
        } else {
          setFields(extractedFields);
          console.log('Edit mode: Using extracted fields from PDF');
        }
      }
    } catch (error) {
      console.error("Error extracting form fields:", error);
    } finally {
      setIsLoadingFields(false);
    }
  };

  // Generate PDF with form fields
  const generatePdfWithFormFields = async () => {
    if (!pdfFileObject) return null;

    setIsGeneratingPdf(true);
    try {
      const arrayBuffer = await pdfFileObject.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const form = pdfDoc.getForm();
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

      // Clear existing form fields to avoid conflicts
      const existingFields = form.getFields();
      existingFields.forEach((field) => {
        try {
          form.removeField(field);
        } catch (error) {}
      });

      // Get first page size for scaling
      const firstPage = pdfDoc.getPage(0);
      const { width: pageWidth, height: pageHeight } = firstPage.getSize();
      
      console.log('PDF Generation - Page dimensions:', {
        pdfPageSize: { pageWidth, pageHeight },
        fixedDimensions: { PDF_WIDTH, PDF_HEIGHT }
      });

    // Create PDF form fields that match overlay positions exactly
    fields.forEach((field, index) => {
      try {
        const pageIndex = field.page - 1;
        const page = pdfDoc.getPage(pageIndex);
        const { width: pWidth, height: pHeight } = page.getSize();
        
        // Apply Y-coordinate transformation for PDF coordinate system
        const x = field.x;
        const y = pHeight - field.y - field.height;
        const width = field.width;
        const height = field.height;
        
        // Create form field
        const fieldName = `${field.type}_${field.id}_${index}`;
        const textField = form.createTextField(fieldName);
        
        textField.addToPage(page, { x, y, width, height });
        textField.setText(field.value || "");
        
        // Make field editable and preserve values
        textField.enableMultiline(false);
        textField.enableRichFormatting(false);
        
        // Remove visual styling but keep functionality
        try {
          textField.acroField.setDefaultAppearance('/Helv 12 Tf 0 g');
        } catch (error) {
          console.warn('Error setting field appearance:', error);
        }
        
      } catch (error) {
        console.error("Error creating form field:", error);
      }
    });

    const pdfBytes = await pdfDoc.save({ useObjectStreams: false, addDefaultPage: false });
    return new Blob([pdfBytes], { type: "application/pdf" });
  } catch (error) {
    console.error("Error generating PDF with form fields:", error);
    return null;
  } finally {
    setIsGeneratingPdf(false);
  }
};


  // Update preview
  const updatePreview = async () => {
    if (!showPreview) return;

    if (fields.length === 0) {
      if (modifiedPdfUrl) {
        URL.revokeObjectURL(modifiedPdfUrl);
        setModifiedPdfUrl(null);
      }
      return;
    }

    const pdfBlob = await generatePdfWithFormFields();
    if (pdfBlob) {
      if (modifiedPdfUrl) {
        URL.revokeObjectURL(modifiedPdfUrl);
      }
      const newUrl = URL.createObjectURL(pdfBlob);
      setModifiedPdfUrl(newUrl);
    }
  };

  // Auto-update preview when fields change
  useEffect(() => {
    if (!showPreview || isReadOnly) return;

    const debounceTimer = setTimeout(() => {
      updatePreview();
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [fields, showPreview, isReadOnly]);

  // Sync fields with parent component
  useEffect(() => {
    if (onFieldsChange && typeof onFieldsChange === 'function') {
      onFieldsChange(fields);
    }
  }, [fields]); // onFieldsChange is intentionally omitted to prevent infinite re-renders

  // Download the PDF with embedded form fields
  const handleDownload = async () => {
    const pdfBlob = await generatePdfWithFormFields();
    if (pdfBlob) {
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pdfName || "document"}_with_fields.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // External drop support: accept dataTransfer with type 'application/x-pdf-field'
  const handleDragOver = (event) => {
    // Only allow drag-over when field manipulation is permitted (admins in edit mode)
    if (!allowFieldManipulation) return;
    if (!pdfContainerRef.current) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    
    // Add visual feedback
    if (pdfContainerRef.current) {
      pdfContainerRef.current.style.backgroundColor = '#e3f2fd';
      pdfContainerRef.current.style.borderColor = '#2196f3';
      pdfContainerRef.current.style.boxShadow = '0 0 10px rgba(33, 150, 243, 0.3)';
    }
  };

  const handleDrop = (event) => {
    console.log("Drop event triggered", event.dataTransfer);

    // Only allow dropping new fields when manipulation is permitted (admins in edit mode)
    if (!allowFieldManipulation) return;
    if (!pdfContainerRef.current) return;
    event.preventDefault();
    
    // Reset visual feedback
    if (pdfContainerRef.current) {
      pdfContainerRef.current.style.backgroundColor = '#f8f9fa';
      pdfContainerRef.current.style.borderColor = '#ddd';
      pdfContainerRef.current.style.boxShadow = 'none';
    }

      try {
      const data = event.dataTransfer.getData('application/x-pdf-field');
      if (!data) {
        console.log("No data found in dataTransfer");
        return;
      }

      let { type = 'text', label, width, height, options } = JSON.parse(data);
      console.log("Parsed field data:", { type, label, width, height });

    // Set default dimensions and labels based on type
    switch (type) {
      case "FirstName":
        label = label || "Name";
        width = width || 150;
        height = height || 30;
        break;
      case "LastName":
        label = label || "Name";
        width = width || 150;
        height = height || 30;
        break;
      case "description":
        label = label || "Text";
        width = width || 150;
        height = height || 30;
        break;
      case "signature":
        label = label || "Signature Field";
        width = width || 200;
        height = height || 80;
        break;
      case "Phone":
        label = label || "Phone Number";
        width = width || 150;
        height = height || 30;
        break;
      case "Email":
        label = label || "Email";
        width = width || 200;
        height = height || 30;
        break;
      case "SSN":
        label = label || "SSN";
        width = width || 150;
        height = height || 30;
        break;
      case "Consent":
        label = label || "Consent";
        width = width || 300;
        height = height || 30;
        break;
      case "Picklist":
        label = label || "Picklist";
        width = width || 200;
        height = height || 30;
        break;
      case "Checkbox":
        label = label || "Checkbox";
        width = 20 || width;
        height = 20 || height;
        break;
      case "Radio":
        label = label || "Checkbox";
        width = 20 || width;
        height = 20 || height;
        break;
      case "Address":
        label = label || "Address";
        width = width || 250;
        height = height || 30;
        break;
      case "Company":
        label = label || "Company";
        width = width || 200;
        height = height || 30;
        break;
      case "Dropdown":
        label = label || "Dropdown";
        width = width || 200;
        height = height || 30;
        break;
      default:
        label = label || type;
        width = width || 150;
        height = height || 30;
    }

    // Get the PDF container's bounding rectangle and scroll position
    const containerRect = pdfContainerRef.current.getBoundingClientRect();
    const containerScrollTop = pdfContainerRef.current.scrollTop;
    
    // Find the actual PDF canvas element for precise positioning
    const canvasElements = pdfContainerRef.current.querySelectorAll('.react-pdf__Page__canvas');
    let targetPage = 1;
    let canvasRect = null;
    
    // Find which canvas was clicked
    for (let i = 0; i < canvasElements.length; i++) {
      const canvas = canvasElements[i];
      const rect = canvas.getBoundingClientRect();
      
      if (event.clientY >= rect.top && event.clientY <= rect.bottom &&
          event.clientX >= rect.left && event.clientX <= rect.right) {
        targetPage = i + 1;
        canvasRect = rect;
        break;
      }
    }
    
    // Fallback to first canvas if none found
    if (!canvasRect && canvasElements.length > 0) {
      canvasRect = canvasElements[0].getBoundingClientRect();
      targetPage = 1;
    }
    
    // Calculate exact position relative to the PDF canvas
    const mouseX = event.clientX - canvasRect.left;
    const mouseY = event.clientY - canvasRect.top;
    
    // Use exact mouse position without centering offset
    let dropX = mouseX;
    let dropY = mouseY;
    
    // Get PDF page dimensions for boundary checking
    const pageWidth = canvasRect?.width ?? getRenderedPageWidth();
    const pageHeight = canvasRect?.height ?? getRenderedPageHeight();

    // Sanitize and clamp to page boundaries
    dropX = Math.max(0, Math.min(dropX, pageWidth - width));
    dropY = Math.max(0, Math.min(dropY, pageHeight - height));
    width = Math.max(10, Math.min(width, pageWidth));
    height = Math.max(10, Math.min(height, pageHeight));
    
    // Debug drop positioning
    console.log(`Drop calculation:`, {
      mousePos: { x: event.clientX, y: event.clientY },
      containerPos: { left: containerRect.left, top: containerRect.top },
      containerScroll: containerScrollTop,
      relativePos: { x: mouseX, y: mouseY },
      targetPage,
      fieldSize: { width, height },
      dropPos: { dropX, dropY },
      pageBounds: { pageWidth, pageHeight }
    });

    const newField = {
      id: `new_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type,
      x: dropX,
      y: dropY,
      width,
      height,
      label,
      value: "",
      page: targetPage,
      isExtracted: false,
      options: options || undefined,
      picklistOptions: options || undefined,
      isRequired: false,
    };

    console.log("Creating new field:", newField);

    setFields((prev) => {
      const updatedFields = [...prev, newField];
      console.log("Updated fields:", updatedFields);
      return updatedFields;
    });
    
    setSelectedField(newField.id);
    setRequiredPromptFieldId(newField.id);
  } catch (e) {
    console.error('Invalid drag data for PDF field:', e);
  }
};


  const handleFieldClick = (fieldId) => {
    setSelectedField(fieldId);
  };

  const handleFieldChange = (fieldId, property, value) => {
    setFields(
      fields.map((field) =>
        field.id === fieldId ? { ...field, [property]: value } : field
      )
    );
  };

  const handleDeleteField = (fieldId) => {
    const targetField = fields.find((f) => f.id === fieldId);
    if (targetField && targetField.isFilled === true) {
      setLockWarningOpen(true);
      return;
    }

    if (!allowFieldManipulation) return;

    setFields(fields.filter((field) => field.id !== fieldId));
    if (selectedField === fieldId) {
      setSelectedField(null);
    }
  };

  // --- Drag Logic ---
const [draggingFieldId, setDraggingFieldId] = useState(null);
const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
const [activePageForDrag, setActivePageForDrag] = useState(null);

const handleFieldMouseDown = (e, field) => {
  if (field.isFilled === true) {
    setLockWarningOpen(true);
    return;
  }

  if (!allowFieldManipulation) return;

  e.stopPropagation();
  e.preventDefault();

  setDraggingFieldId(field.id);
  setActivePageForDrag(field.page || 1);

  // Get bounding box of the target page’s canvas
  const canvasElements =
    pdfContainerRef.current?.querySelectorAll(".react-pdf__Page__canvas") || [];
  const targetCanvas = canvasElements[(field.page || 1) - 1];
  if (!targetCanvas) return;

  const rect = targetCanvas.getBoundingClientRect();
  const offsetX = e.clientX - rect.left - field.x;
  const offsetY = e.clientY - rect.top - field.y;

  setDragOffset({ x: offsetX, y: offsetY });

  // Add listeners globally so dragging works smoothly
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
};

const handleMouseMove = (e) => {
  if (!draggingFieldId || !activePageForDrag) return;

  const canvasElements =
    pdfContainerRef.current?.querySelectorAll(".react-pdf__Page__canvas") || [];
  const targetCanvas = canvasElements[activePageForDrag - 1];
  if (!targetCanvas) return;

  const rect = targetCanvas.getBoundingClientRect();
  const pageWidth = rect.width;
  const pageHeight = rect.height;

  // Calculate new coordinates relative to the same page
  const newX = e.clientX - rect.left - dragOffset.x;
  const newY = e.clientY - rect.top - dragOffset.y;

  // Keep the field inside the page boundaries
  const boundedX = Math.max(0, Math.min(newX, pageWidth - 30)); // 30px buffer
  const boundedY = Math.max(0, Math.min(newY, pageHeight - 20));

  // Update field coordinates only within the same page
  setFields((fields) =>
    fields.map((f) =>
      f.id === draggingFieldId
        ? {
            ...f,
            x: boundedX,
            y: boundedY,
            page: activePageForDrag, // Stay on same page
          }
        : f
    )
  );
};

const handleMouseUp = () => {
  setDraggingFieldId(null);
  setActivePageForDrag(null);
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
};


  useEffect(() => {
    if (draggingFieldId !== null) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [draggingFieldId, dragOffset, allowFieldManipulation]);

  // Handle window resize for responsive PDF display
  useEffect(() => {
    const handleResize = () => {
      // Force re-render when window resizes
      setFields(prev => [...prev]);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Resize Logic
  const [resizingFieldId, setResizingFieldId] = useState(null);
  const [resizeStart, setResizeStart] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const handleResizeMouseDown = (e, field) => {
    if (field.isFilled === true) {
      setLockWarningOpen(true);
      return;
    }

    if (!allowFieldManipulation) return;

    e.stopPropagation();
    setResizingFieldId(field.id);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: field.width,
      height: field.height,
    });
  };

  const handleResizeMouseMove = (e) => {
    if (resizingFieldId !== null) {
      setFields((fields) =>
        fields.map((f) =>
          f.id === resizingFieldId
            ? {
                ...f,
                width: Math.max(
                  10,
                  Math.min(getRenderedPageWidth(), resizeStart.width + (e.clientX - resizeStart.x))
                ),
                height: Math.max(
                  10,
                  Math.min(getRenderedPageHeight(), resizeStart.height + (e.clientY - resizeStart.y))
                ),
              }
            : f
        )
      );
    }
  };

  const handleResizeMouseUp = () => {
    setResizingFieldId(null);
  };

  useEffect(() => {
    if (resizingFieldId !== null && allowFieldManipulation) {
      document.addEventListener("mousemove", handleResizeMouseMove);
      document.addEventListener("mouseup", handleResizeMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleResizeMouseMove);
        document.removeEventListener("mouseup", handleResizeMouseUp);
      };
    }
  }, [resizingFieldId, resizeStart, allowFieldManipulation]);

  const handleSave = async () => {
    // if (!pdfFileObject || !pdfName?.trim()) {
    //   alert("Please upload a PDF and provide a name");
    //   return;
    // }

    setIsSaving(true);
    try {
      const pdfBlob = await generatePdfWithFormFields();
      if (pdfBlob) {
        const newPdfFile = new File([pdfBlob], `${pdfName}_updated.pdf`, {
          type: "application/pdf",
        });

        if (onSave) {
          onSave(newPdfFile, {
            fields: fields,
            fieldValues: fields.reduce((acc, field) => {
              acc[field.id] = field.value;
              return acc;
            }, {}),
            isTemplateUpdate: true
          });
        }

        // Immediately display the saved PDF and clear overlays to prevent double-render boxes
        try {
          const objectUrl = URL.createObjectURL(newPdfFile);
          setModifiedPdfUrl(objectUrl);
          setIsPdfSaved(true); // Mark as saved to hide overlay fields
          setSelectedField(null);
          setPdfFileObject(newPdfFile);
        } catch (_) {}

        if (onClose) {
          onClose();
        }
      }
    } catch (error) {
      console.error("Error saving PDF:", error);
      alert("Error saving PDF. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      if (modifiedPdfUrl) {
        URL.revokeObjectURL(modifiedPdfUrl);
      }
      // Cleanup any created object URLs
      if (pdfFileObject && pdfFileObject instanceof File) {
        const objectUrl = URL.createObjectURL(pdfFileObject);

        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [modifiedPdfUrl, pdfFileObject]);

  // Show loading state while PDF is being fetched
  if (isLoadingPdf) {
    return (
      <div className="pdf-wrapper" style={{ textAlign: 'center', padding: '20px' }}>
        <p>Loading PDF...</p>
      </div>
    );
  }

  // Show error state if no PDF file is available
  if (!pdfFileObject) {
    return (
      <div className="pdf-wrapper" style={{ textAlign: 'center', padding: '20px' }}>
        <p>No PDF file available</p>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
          Please upload a PDF file to start editing
        </p>
        {onClose && (
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
        )}
      </div>
    );
  }

  // Show error state if PDF failed to load
  if (pdfLoadError) {
    console.log("pdfFileObject ", pdfFileObject?.File); 
    return (
      <div className="pdf-wrapper" style={{ textAlign: 'center', padding: '20px' }}>
        <p style={{ color: '#d32f2f', marginBottom: '16px' }}>Failed to load PDF file</p>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '16px' }}>
          Please check the file format and try again
        </p>

        {/* Fallback PDF display using iframe */}
        {pdfFileObject && (
          <div style={{ marginTop: '20px', border: '1px solid #ddd', borderRadius: '4px' }}>
            <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
              Attempting fallback display...
            </p>
            <iframe
              src={URL.createObjectURL(pdfFileObject)}
              width="100%"
              height="600px"
              title="PDF Fallback"
              style={{ border: 'none' }}
            />
          </div>
        )}

        <Button
          variant="outlined"
          onClick={() => {
            setPdfLoadError(null);
            setPdfFileObject(null);
            // Force re-render
            setTimeout(() => setPdfFileObject(pdfFile), 100);
          }}
          style={{ marginTop: '16px' }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const isFieldLocked = (field) => field?.isFilled === true;

  const canManipulateField = (field) => allowFieldManipulation && !isFieldLocked(field);
  const canEditField = (field) => allowFieldEditing && !isFieldLocked(field);

  return (
    <div className="pdf-wrapper">
      {renderLockWarningDialog()}

      {isReadOnly && (
        <div
          style={{
            padding: "10px",
            background: "#e3f2fd",
            borderRadius: "4px",
            marginBottom: "10px",
          }}
        >
          Loading existing form fields...
        </div>
      )}

      {isLoadingPdf && (
        <div
          style={{
            padding: "10px",
            background: "#fff3cd",
            borderRadius: "4px",
            marginBottom: "10px",
            border: "1px solid #ffeaa7",
          }}
        >
          Loading PDF file...
        </div>
      )}

      {/* Top row controls: only Save and Download */}
      <div className="flex justify-between items-center top-btn-row">
        <div>
          <Button
            className="pdf-primary-button btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
            style={{ marginRight: "10px" }}
          >
            {isSaving ? "Saving..." : "Save PDF"}
          </Button>

          <Button
            className="pdf-primary-button btn btn-success"
            onClick={handleDownload}
            disabled={isGeneratingPdf || fields.length === 0}
            style={{ marginRight: "10px" }}
          >
            {isGeneratingPdf ? "Generating..." : "Download PDF with Fields"}
          </Button>
        </div>
        <div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              title="Close Editor"
            >
              <ClearIcon style={{ color: "#000" }} />
            </button>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: "20px" }}>
        {/* PDF Editor Section */}
        <div style={{ flex: 1, width: "100% "}}>
           <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{!allowFieldEditing ? "Form" : "Editor"} ({fields.length} fields)</span>
                      {numPages && <span style={{ fontSize: '14px', color: '#161313ff' }}>Page {currentPage} of {numPages}</span>}
                    </h3>
          <div
            ref={pdfContainerRef}
            className="pdf-viewer"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onScroll={(e) => {
              // Calculate current page based on scroll position
              const container = e.currentTarget;
              const scrollTop = container.scrollTop;
              const pageElements = container.getElementsByClassName('react-pdf__Page');
              
              for (let i = 0; i < pageElements.length; i++) {
                const element = pageElements[i];
                const rect = element.getBoundingClientRect();
                if (rect.top + container.scrollTop >= scrollTop - rect.height / 2) {
                  setCurrentPage(i + 1);
                  break;
                }
              }
            }}
            onDragLeave={(e) => {
              // Reset visual feedback when dragging away
              if (pdfContainerRef.current) {
                pdfContainerRef.current.style.backgroundColor = '#f8f9fa';
                pdfContainerRef.current.style.borderColor = '#ddd';
                pdfContainerRef.current.style.boxShadow = 'none';
              }
            }}
            style={{
              position: "relative",
              cursor: "default",
              minHeight: 600,
              width: "100%",
              border: "1px solid #ddd",
              marginBottom: "20px",
              overflowY: "auto",
              overflowX: "hidden",
              maxHeight: "75vh",
              padding: "20px",
              backgroundColor: "#f8f9fa",
              zIndex: 0,
            }}
          >
            <Document
              file={pdfFileObject}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(error) => {
                console.error('PDF load error:', error);
                setPdfLoadError(error);
                setIsLoadingPdf(false);
              }}
              loading={
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#666'
                }}>
                  <p>Loading PDF...</p>
                </div>
              }
              error={
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#d32f2f',
                  flexDirection: 'column'
                }}>
                  <p>Failed to load PDF file</p>
                  <p style={{ fontSize: '12px', marginTop: '8px', marginBottom: '16px' }}>
                    Please check the file format and try again
                  </p>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setPdfLoadError(null);
                      // Force re-render
                      setPdfFileObject(null);
                      setTimeout(() => setPdfFileObject(pdfFile), 100);
                    }}
                  >
                    Retry
                  </Button>
                </div>
              }
              noData={
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#666'
                }}>
                  <p>No PDF data available</p>
                </div>
              }
              onSourceError={(error) => {
                console.error('PDF source error:', error);
                setPdfLoadError(error);
                setIsLoadingPdf(false);
              }}
            >
              {numPages ? Array.from(new Array(numPages), (el, index) => (
                <div key={`page_${index + 1}`} data-page-number={index + 1} style={{
                  marginBottom: "20px",
                  position: "relative",
                  width: "100%",
                  display: "flex",
                  justifyContent: "center"
                }}>
                   <div style={{ position: 'relative' }}>                                   
                  <Page
                    pageNumber={index + 1}
                    width={getRenderedPageWidth()}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '200px',
                        color: '#666'
                      }}>
                        <p>Loading page {index + 1}...</p>
                      </div>
                    }
                    error={
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '200px',
                        color: '#d32f2f'
                      }}>
                        <p>Failed to load page {index + 1}</p>
                      </div>
                    }
                  />

                  {/* Page number badge */}
                  <div style={{
                      position: 'absolute',
                      bottom: 5,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: 'black',
                      padding: '2px 6px',
                      borderRadius: 5,
                      fontSize: 14,
                      fontWeight: 500,
                      zIndex: 1000,
                      boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                      border: '1px solid rgba(255,255,255,0.2)',
                    }}>
                      {index + 1}
                    </div>
                  </div>

                  {/* Page-specific overlay fields - single rendering for both add and edit */}
                  {fields
                    .filter((f) => (f.page || 1) === index + 1)
                    .map((field) => {
                      // Position field absolutely within the page wrapper
                      const isRequired = field.isRequired === true;
                      const locked = isFieldLocked(field);
                      const containerStyles = {
                        position: "absolute",
                        left: `${field.x}px`,
                        top: `${field.y}px`,
                        width: `${field.width}px`,
                        height: `${field.height}px`,
                        border: "none",
                        background: "transparent",
                        zIndex: 10,
                        cursor: locked ? "not-allowed" : (allowFieldManipulation ? "move" : "default"),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxSizing: "border-box",
                        padding: 0,
                        overflow: "hidden",
                        opacity: locked ? 0.7 : 1,
                        pointerEvents: "auto",
                      };

                      // Local input styles for overlay inputs
                      const inputStyles = {
                        width: "100%",
                        height: "100%",
                        border: "1px solid #BDBDBD",
                        background: "#FFFFFF",
                        fontSize: "12px",
                        padding: "2px 4px",
                        boxSizing: "border-box",
                        outline: "none",
                        color: "#000000",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      };

                      // Render same switch UI as below but scoped here
                      let fieldElement;
                      switch (field.type) {
                        case "FirstName":
                        case "LastName":
                        case "description":
                        case "Phone":
                        case "Email":
                          fieldElement = (
                            <input
                              type="text"
                              value={field.value ?? ""}
                              placeholder={(field.value ?? "") === "" ? (field.isExtracted ? field.originalFieldName : (field.label || "")) : ""}
                              readOnly={!canEditField(field)}
                              onChange={(e) => handleFieldChange(field.id, "value", e.target.value)}
                              style={inputStyles}
                            />
                          );
                          break;
                          
                        case "Stamp":
                          fieldElement = (
                            <div style={{
                              ...inputStyles,
                              border: "2px dashed #666",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#fff9c4",
                              fontWeight: "bold",
                              fontSize: "14px"
                            }}>
                              {field?.label || "STAMP"}
                            </div>
                          );
                          break;
                          
                        case "Formula":
                          fieldElement = (
                            <input
                              type="text"
                              value={field.expression || ""}
                              placeholder={field.expression ? "" : "Enter formula (e.g., =A1+B1)"}
                              readOnly={!allowFieldEditing}
                              onChange={(e) => handleFieldChange(field.id, "expression", e.target.value)}
                              style={{
                                ...inputStyles,
                                fontStyle: "italic",
                                backgroundColor: "#e8f5e9"
                              }}
                            />
                          );
                          break;
                          
                        
                          
                        case "DateTime":
                          fieldElement = (
                            <input
                              type={field.includeTime ? "datetime-local" : "date"}
                              value={field.value || ""}
                              readOnly={!allowFieldEditing}
                              onChange={(e) => handleFieldChange(field.id, "value", e.target.value)}
                              style={{
                                ...inputStyles,
                                textAlign: "center"
                              }}
                            />
                          );
                          break;

                        case "Radio":
                          fieldElement = (
                            <div style={{
                              ...inputStyles,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'transparent',
                              border: 'none',
                              padding: 0,
                              width: '20px',
                              height: '20px'
                            }}>
                              <div style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '50%',
                                border: '2px solid #666',
                                backgroundColor: field.value ? '#666' : 'transparent',
                                cursor: 'pointer'
                              }} />
                            </div>
                          );
                          break;

                        case "Checkbox":
                          fieldElement = (
                            <div style={{
                              ...inputStyles,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'transparent',
                              border: 'none',
                              padding: 0,
                              width: '20px',
                              height: '20px'
                            }}>
                              <div style={{
                                width: '100%',
                                height: '100%',
                                border: '2px solid #666',
                                backgroundColor: field.value ? '#666' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '14px',
                                lineHeight: 1,
                                cursor: 'pointer'
                              }}>
                                {field.value ? '✓' : ''}
                              </div>
                            </div>
                          );
                          break;                        case "Attachment":
                          fieldElement = (
                            <div style={{
                              ...inputStyles,
                              border: "2px dashed #666",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#e3f2fd",
                              cursor: "pointer"
                            }}>
                              <span style={{ fontSize: "24px" }}>📎</span>
                              <span style={{ fontSize: "10px", marginTop: "4px" }}>Click to attach file</span>
                            </div>
                          );
                          break;
                        case "Address":
                        case "SSN":
                        case "Company":
                        case "Consent":
                          fieldElement = (
                            <input
                              type="text"
                              value={field.value ?? ""}
                              placeholder={(field.value ?? "") === "" ? (field.isExtracted ? field.originalFieldName : (field.label || "")) : ""}
                              readOnly={!allowFieldEditing}
                              onChange={(e) => handleFieldChange(field.id, "value", e.target.value)}
                              style={inputStyles}
                            />
                          );
                          break;
                          
                        case "DateTime":                      
                        case "Address":
                        case "SSN":
                        case "Company":
                        case "Consent":
                          fieldElement = (
                            <input
                              type="text"
                              value={field.value ?? ""}
                              placeholder={(field.value ?? "") === "" ? (field.isExtracted ? field.originalFieldName : (field.label || "")) : ""}
                              readOnly={!allowFieldEditing}
                              onChange={(e) => handleFieldChange(field.id, "value", e.target.value)}
                              style={inputStyles}
                            />
                          );
                          break;
                          
                        case "DateTime":
                          
                        case "signature":
                          fieldElement = (
                            <div style={{ ...inputStyles, padding: 0, display: "flex", flexDirection: "column" }}>
                              {/* Text input option */}
                              <input
                                type="text"
                                value={field.value ?? ""}
                                placeholder={field.value ? "" : "Type signature or draw below"}
                                readOnly={!allowFieldEditing}
                                onChange={(e) => handleFieldChange(field.id, "value", e.target.value)}
                                style={{
                                  width: "100%",
                                  height: "50%",
                                  border: "none",
                                  borderBottom: "1px solid #ddd",
                                  background: "transparent",
                                  fontSize: "10px",
                                  padding: "2px 4px",
                                  outline: "none",
                                  fontFamily: "cursive"
                                }}
                              />
                              {/* Canvas for drawing signature */}
                              <canvas
                                ref={(canvas) => {
                                  if (canvas && allowFieldEditing) {
                                    const ctx = canvas.getContext('2d');
                                    let isDrawing = false;
                                    
                                    canvas.width = field.width - 8;
                                    canvas.height = (field.height / 2) - 4;
                                    
                                    const startDrawing = (e) => {
                                      isDrawing = true;
                                      const rect = canvas.getBoundingClientRect();
                                      ctx.beginPath();
                                      ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
                                    };
                                    
                                    const draw = (e) => {
                                      if (!isDrawing) return;
                                      const rect = canvas.getBoundingClientRect();
                                      ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
                                      ctx.stroke();
                                      // Update field value to indicate signature drawn
                                      handleFieldChange(field.id, "value", "[Signature Drawn]");
                                    };
                                    
                                    const stopDrawing = () => {
                                      isDrawing = false;
                                    };
                                    
                                    canvas.addEventListener('mousedown', startDrawing);
                                    canvas.addEventListener('mousemove', draw);
                                    canvas.addEventListener('mouseup', stopDrawing);
                                    canvas.addEventListener('mouseout', stopDrawing);
                                    
                                    // Clear canvas button
                                    canvas.addEventListener('dblclick', () => {
                                      ctx.clearRect(0, 0, canvas.width, canvas.height);
                                      handleFieldChange(field.id, "value", "");
                                    });
                                  }
                                }}
                                style={{
                                  width: "100%",
                                  height: "50%",
                                  border: "1px solid #ddd",
                                  borderTop: "none",
                                  cursor: allowFieldEditing ? "crosshair" : "default",
                                  backgroundColor: "#fafafa"
                                }}
                                title={allowFieldEditing ? "Draw signature here (double-click to clear)" : "Signature area"}
                              />
                            </div>
                          );
                          break;

                        case "Picklist":
                        case "Dropdown":
                          // Get options from field or use default options
                          const fieldOptions = field.options || field.picklistOptions || [];
                          fieldElement = (
                            <select
                              value={field.value ?? ""}
                              disabled={!canEditField(field)}
                              onChange={(e) => handleFieldChange(field.id, "value", e.target.value)}
                              style={{
                                ...inputStyles,
                                textAlign: "left",
                                height: "100%",
                                lineHeight: `${Math.max(12, (field.height || 28) - 6)}px`,
                                padding: "0 6px",
                                WebkitAppearance: "none",
                                MozAppearance: "none",
                                appearance: "none",
                                cursor: allowFieldEditing ? "pointer" : "default"
                              }}
                            >
                              <option value="" disabled>
                                {field.label || "Select..."}
                              </option>
                              {fieldOptions.length > 0 ? (
                                fieldOptions.map((opt, idx) => (
                                  <option key={idx} value={typeof opt === 'string' ? opt : opt.value || opt.label}>
                                    {typeof opt === 'string' ? opt : opt.label || opt.value}
                                  </option>
                                ))
                              ) : (
                                <option value="" disabled>No options available</option>
                              )}

                          {locked && (
                            <span
                              style={{
                                position: "absolute",
                                bottom: -12,
                                right: 0,
                                fontSize: 10,
                                color: "#9e9e9e",
                                background: "rgba(255,255,255,0.8)",
                                padding: "0 2px",
                              }}
                            >
                              locked
                            </span>
                          )}
                            </select>
                          );
                          break;

                        case "Checkbox":
                          fieldElement = (
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              {field.options?.map((opt, idx) => (
                                <label key={idx} style={{ fontSize: 12 }}>
                                  <input
                                    type="checkbox"
                                    checked={Array.isArray(field.value) ? field.value.includes(opt) : false}
                                    disabled={!canEditField(field)}
                                    onChange={(e) => {
                                      let newValue = Array.isArray(field.value) ? [...field.value] : [];
                                      if (e.target.checked && !newValue.includes(opt)) newValue.push(opt);
                                      if (!e.target.checked) newValue = newValue.filter((v) => v !== opt);
                                      handleFieldChange(field.id, "value", newValue);
                                    }}
                                  />{" "}
                                  {opt}
                                </label>
                              ))}
                            </div>
                          );
                          case "Radio":
  fieldElement = (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: "4px",
      padding: "4px",
      backgroundColor: "transparent",
      border: "none",
      width: "100%",
      height: "100%",
      overflow: "visible"
    }}>
      {field.options?.map((option, optIdx) => (
        <div 
          key={optIdx}
          style={{
            display: "flex",
            alignItems: "center",
            cursor: allowFieldEditing ? "pointer" : "default",
            padding: "4px 0",
            whiteSpace: "nowrap"
          }}
          onClick={() => {
            if (allowFieldEditing) {
              handleFieldChange(field.id, "value", option);
            }
          }}
        >
          <div style={{
            width: "16px",
            height: "16px",
            borderRadius: "50%",
            border: "2px solid #1976d2",
            marginRight: "8px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: field.value === option ? "#1976d2" : "white"
          }}>
            {field.value === option && (
              <div style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                backgroundColor: "white"
              }} />
            )}
          </div>
          <span style={{ 
            fontSize: "12px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}>{option}</span>
        </div>
      ))}
    </div>
  );
  break;

                        default:
                          fieldElement = null;
                      }

                      if (!fieldElement) return null;

                      return (
                        <div
                          key={field.id}
                          style={containerStyles}
                          title=""
                          onMouseDown={(e) => allowFieldManipulation && handleFieldMouseDown(e, field)}
                          onClick={() => handleFieldClick(field.id)}
                        >
                          {fieldElement}

                          {isRequired && (
                            <span
                              style={{
                                position: "absolute",
                                top: -10,
                                left: 0,
                                color: "#d32f2f",
                                fontSize: 12,
                                fontWeight: "bold",
                              }}
                            >
                              *
                            </span>
                          )}

                          {/* Delete button and resize handle only in admin edit mode */}
                          {allowFieldManipulation && !locked && (
                            <>
                              {/* Delete button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteField(field.id);
                                }}
                                style={{
                                  position: "absolute",
                                  top: -8,
                                  right: -8,
                                  background: "#f44336",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "50%",
                                  width: 18,
                                  height: 18,
                                  cursor: "pointer",
                                  lineHeight: "18px",
                                  textAlign: "center",
                                  fontSize: 10,
                                }}
                                title="Remove"
                              >
                                ×
                              </button>

                              {/* Resize handle (bottom-right) */}
                              <div
                                onMouseDown={(e) => handleResizeMouseDown(e, field)}
                                style={{
                                  position: "absolute",
                                  right: -4,
                                  bottom: -4,
                                  width: 10,
                                  height: 10,
                                  background: "#2196f3",
                                  cursor: "nwse-resize",
                                  borderRadius: 2,
                                }}
                                title="Resize"
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                </div>
              )) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '200px',
                  color: '#666'
                }}>
                  <p>Preparing PDF...</p>
                </div>
              )}
            </Document>
          </div>

          {/* Pagination removed - now using vertical scroll for all pages */}
        </div>

        {allowFieldManipulation && requiredPromptFieldId && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.25)",
              zIndex: 2000,
            }}
          >
            <div
              style={{
                backgroundColor: "#ffffff",
                borderRadius: 8,
                padding: 16,
                maxWidth: 360,
                width: "90%",
                boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
                fontSize: 14,
              }}
            >
              <div style={{ marginBottom: 8, fontWeight: 600 }}>Should this field be required?</div>
              <div style={{ marginBottom: 16, color: "#4b5563", fontSize: 13 }}>
                This will make this field mandatory for clinicians before they can save the form.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button
                  onClick={() => {
                    setFields((prev) =>
                      prev.map((f) =>
                        f.id === requiredPromptFieldId ? { ...f, isRequired: false } : f
                      )
                    );
                    setRequiredPromptFieldId(null);
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 4,
                    border: "1px solid #d1d5db",
                    backgroundColor: "#ffffff",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  No
                </button>
                <button
                  onClick={() => {
                    setFields((prev) =>
                      prev.map((f) =>
                        f.id === requiredPromptFieldId ? { ...f, isRequired: true } : f
                      )
                    );
                    setRequiredPromptFieldId(null);
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 4,
                    border: "none",
                    backgroundColor: "#2563eb",
                    color: "#ffffff",
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  Yes, Required
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfEditorAdmin;